import { Box, Container, Flex, Heading, Stack, Text } from '@chakra-ui/react'

import { makeServerProps } from '@/server/query'

import CourseQ from '@/queries/CourseQ'
import { COLLEGE_ALGEBRA_COURSE } from '@/queries/constants'
import TopicQ from '@/queries/TopicQ'
import { useQuery } from '@/client/context/query'
import { SQL } from '@/client/query'
import makePage from '@/client/services/pages/makePage'
import { faq, getCertificationUrl, getUrl } from '@/client/services/url'

import CollegeAlgebraWarning from '@/components/Certification/CollegeAlgebraWarning'
import CourseFinder, { useCourse } from '@/components/CourseFinder'
import FAQ from '@/components/FAQ/FAQ'
import Layout from '@/components/Layout/Layout'
import ShwLink from '@/components/ShwLink'
import TutorCertificationList from '@/components/Tutor/TutorCertificationList'
import LinkableDiv from '@/components/UI/LinkableDiv'
import HowItWorks from '@/components/Worlds/Certify/internal/HowItWorks'
import WhyGetCertified from '@/components/Worlds/Certify/internal/WhyGetCertified'

const FAQ_ID = 'faq'

const FAQ_ITEMS = [
  {
    question: 'What are the different options for certification?',
    answer: () => (
      <Text>
        If you&apos;d like to get certified in a math or science subject, then you may request a
        certification by submitting a video of yourself working on the appropriate Khan Academy
        test. For SAT, AP, and selected other topics, you may instead request an{' '}
        <b>honorary certification</b> by submitting an explanation and documentation of your prior
        experience.
      </Text>
    ),
  },
  {
    question: 'Are honorary certifications included on your transcript/portfolio?',
    answer: () => (
      <Text>
        No. Video certifications are featured on the portfolio to demonstrate mastery of subject
        material, along with tutoring statistics. Honorary certifications on Schoolhouse are granted
        based on SAT scores and AP exam scores, which students submit directly to colleges. Since
        they don&apos;t give colleges new information, we don&apos;t include them on the portfolio.
      </Text>
    ),
  },
  {
    question: 'What certification do I need to become a tutor in a subject?',
    answer: () => (
      <Text>
        Generally, you can become a tutor in a subject after getting certified in at least one topic
        or completing the course challenge. To tutor our programs (such as SAT Bootcamp), you need
        to complete a couple extra steps. You can see the complete steps on the{' '}
        <ShwLink href={getUrl('tutor')}>tutor tab</ShwLink>.
      </Text>
    ),
  },
  {
    question: 'How do Course Challenges work?',
    answer: `If you get certified in a Course Challenge, that means you're eligible to tutor in any one of
                        the units in the course. There is no need to also get certified within each unit if you've done the
                        Course Challenge.`,
  },
  {
    question: 'What do I do if I have other questions?',
    answer: () => (
      <Text>
        Please visit our <ShwLink href={faq.getUrl('certification')}>Help Desk</ShwLink>. If you
        still have unanswered questions, please start a chat request.
      </Text>
    ),
  },
]

function CertificationFAQ(): JSX.Element {
  return (
    <LinkableDiv id={FAQ_ID}>
      <FAQ items={FAQ_ITEMS} category="certification" justifyHeader="start" />
    </LinkableDiv>
  )
}

function TutorCertification(): JSX.Element {
  const query = useQuery()
  // Show all courses which have topics that require certification, matching the logic below which queries for topics.
  const { course, courses } = useCourse({
    includeAllTopics: false,
    excludeOther: false,
    customWhere: SQL`EXISTS(SELECT 1 ${CourseQ.getCertificationTopics()})`,
  })

  const currentCourse = query.one(course == null ? CourseQ.getDefault() : CourseQ.getByName(course))
  const currentCourseName = currentCourse(CourseQ.name)

  const topics = currentCourse.many(
    TopicQ.where(
      {
        courseId: CourseQ.id,
        order: [[TopicQ.weight, 'desc']],
      },
      TopicQ.requiresCertification()
    )
  )

  return (
    <Layout
      title="Become a Certified Tutor"
      meta={{
        description:
          'Join hundreds of passionate tutors making an impact. Get certified with Schoolhouse and start sharing your knowledge today',
      }}
    >
      <Container>
        <Box bgColor="bg.info" borderRadius="16px" p="32px" mb="32px">
          <Heading size="2xl" as="h1">
            Get Certified
          </Heading>
          <Text mt={4}>
            Schoolhouse certifications allow you to demonstrate your mastery of core topics and
            begin tutoring. They&apos;ll be featured on your Schoolhouse portfolio, which you can
            send to our college partners (such as MIT and UChicago) through an online link.
          </Text>
          <Text mt="8px">
            {' '}
            Have some questions before getting started? Learn more about certifications{' '}
            <ShwLink href={getCertificationUrl('about')}>here</ShwLink> or check out our{' '}
            <ShwLink href={{ pathname: getCertificationUrl('get-certified'), hash: FAQ_ID }}>
              FAQs
            </ShwLink>{' '}
            below.
          </Text>
        </Box>
        <Stack spacing="36px">
          <Stack
            justifyContent="space-between"
            direction={['column', 'row']}
            align="center"
            spacing="16px"
          >
            <Heading size="xl">Topics in {currentCourseName}</Heading>
            <CourseFinder courses={courses} course={course} text="Course" />
          </Stack>
          {currentCourseName === COLLEGE_ALGEBRA_COURSE ? (
            <Box mt="12px">
              <CollegeAlgebraWarning />
            </Box>
          ) : null}
          <Flex mt="24px" flexDirection="column" mb="36px">
            <TutorCertificationList topics={topics} />
          </Flex>
          <HowItWorks />
          <WhyGetCertified />
          <CertificationFAQ />
        </Stack>
      </Container>
    </Layout>
  )
}

const TutorCertificationPage = makePage(TutorCertification, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TutorCertificationPage)
export default TutorCertificationPage
